import React, { useImperativeHandle } from "react";
import { FComptetUser } from "../../../../../../interfaces/UserInterface";
import { TextField } from "@mui/material";
import { InputInterface } from "../../../../../../interfaces/InputInterface";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import { LoadingButton } from "@mui/lab";
import { requestApi } from "../../../../../../helpers/RequestApi";
import { PATCH } from "../../../../../../utils/MethodUtils";
import { USER_PROP_URL } from "../../../../../../utils/UrlsUtils";
import { toastr } from "react-redux-toastr";
import getErrorApi from "../../../../../../helpers/GetErrorApi";
import { useAppSelector } from "../../../../../../app/hooks";
import { RootState } from "../../../../../../app/store";

interface State {
  fComptetUser: FComptetUser | undefined;
  setFComptetUser: Function;
}

interface FormState {
  nbDayDeletePl: InputInterface;
  checked: InputInterface;
}

const UserFormNbDayTransformPlInDeComponent = React.memo(
  React.forwardRef(({ fComptetUser, setFComptetUser }: State, ref) => {
    const { t } = useTranslation();
    const getDefaultValues = React.useCallback((): FormState => {
      return {
        nbDayDeletePl: {
          value: fComptetUser?.nbDayDeletePl ?? "",
          error: "",
        },
        checked: {
          value: typeof fComptetUser?.nbDayDeletePl === "number",
          error: "",
        },
      };
    }, [fComptetUser?.nbDayDeletePl]);
    const [values, setValues] = React.useState<FormState>(getDefaultValues());
    const [loading, setLoading] = React.useState(false);
    const token = useAppSelector((state: RootState) => state.globalState.token);

    const handleChangeCheckbox = React.useCallback(
      (prop: keyof FormState) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
          setValues((v) => {
            return {
              ...v,
              [prop]: { ...v[prop], value: event.target.checked, error: "" },
            };
          });
        },
      []
    );

    const handleChange = React.useCallback(
      (prop: keyof FormState) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
          setValues((v) => {
            return {
              ...v,
              [prop]: { ...v[prop], value: event.target.value, error: "" },
            };
          });
        },
      []
    );

    const getValue = React.useCallback(() => {
      return {
        nbDayDeletePl: values.checked.value
          ? parseInt(values.nbDayDeletePl.value)
          : null,
      };
    }, [values.checked.value, values.nbDayDeletePl.value]);

    const save = React.useCallback(async () => {
      setLoading(true);
      const response = await requestApi({
        method: PATCH,
        path: USER_PROP_URL + "/" + fComptetUser?.userPropId,
        allowError: true,
        token: token,
        body: getValue(),
      });
      if (response.statusCode === 200) {
        toastr.success(
          t("word.success"),
          t("sentence.notification.user_updated")
        );
        setFComptetUser((x: FComptetUser | undefined) => {
          if (!x) {
            return x;
          }
          x.nbDayDeletePl = response.content.nbDayDeletePl;
          return { ...x };
        });
      } else if (response.statusCode === 401) {
        toastr.info(t("word.info"), t("error.reconnect"));
      } else {
        for (let message of getErrorApi(response.content)) {
          toastr.error(t("word.error"), t(message));
        }
      }
      setLoading(false);
    }, [fComptetUser?.userPropId, getValue, setFComptetUser, t, token]);

    useImperativeHandle(ref, () => ({
      getValue() {
        return getValue();
      },
    }));

    React.useEffect(() => {
      setValues(getDefaultValues());
    }, [fComptetUser?.nbDayDeletePl]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <Box sx={{ marginTop: 1, display: "flex" }}>
        <Checkbox
          checked={values.checked.value}
          onChange={handleChangeCheckbox("checked")}
        />
        <TextField
          fullWidth={true}
          autoComplete="off"
          error={!!values.nbDayDeletePl.error}
          disabled={!values.checked.value}
          helperText={t(values.nbDayDeletePl.error ?? "")}
          sx={{ width: "100%" }}
          required
          type="number"
          value={values.checked.value ? values.nbDayDeletePl.value : ""}
          onChange={handleChange("nbDayDeletePl")}
          label={t("field.nbDayDeletePl")}
        />
        <LoadingButton variant="contained" loading={loading} onClick={save}>
          {t("word.validate")}
        </LoadingButton>
      </Box>
    );
  })
);

export default UserFormNbDayTransformPlInDeComponent;
